/**
 * User Management Page
 * CRUD operations for system users
 */

import React, { useState, useMemo } from 'react';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Eye,
  Users,
  Shield,
  ClipboardCheck,
  User,
  Mail,
  Building,
  Calendar,
  Power,
  PowerOff,
} from 'lucide-react';
import { useBEStudy } from '@/hooks/useBEStudy';
import { DataTable } from '@/components/be-study/common/DataTable';
import { StatusBadge, RoleBadge, UserStatusBadge } from '@/components/be-study/common/StatusBadge';
import { AddUserModal } from '@/components/be-study/modals/AddUserModal';
import { DeleteConfirmationModal } from '@/components/be-study/modals/ConfirmationModal';
import { USER_ROLES, USER_STATUS, formatDate, getRelativeTime } from '@/constants/beStudyMockData';
import { cn } from '@/lib/utils';

/**
 * User Detail Modal
 */
function UserDetailModal({ user, open, onOpenChange }) {
  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" data-testid="user-detail-modal">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user.avatar} />
              <AvatarFallback className="text-lg">
                {user.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <DialogTitle>{user.name}</DialogTitle>
              <DialogDescription>{user.email}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <div className="mt-1">
                <RoleBadge role={user.role} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <div className="mt-1">
                <UserStatusBadge status={user.status} />
              </div>
            </div>
          </div>

          {user.department && (
            <div>
              <Label className="text-xs text-muted-foreground">Department</Label>
              <p className="text-sm mt-1 flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                {user.department}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Created</Label>
              <p className="text-sm mt-1">{formatDate(user.createdAt)}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Last Login</Label>
              <p className="text-sm mt-1">
                {user.lastLogin ? getRelativeTime(user.lastLogin) : 'Never'}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Edit User Modal
 */
function EditUserModal({ user, open, onOpenChange, onSave }) {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || '',
    department: user?.department || '',
  });

  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        department: user.department || '',
      });
    }
  }, [user]);

  const handleSave = () => {
    onSave?.(formData);
    onOpenChange(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" data-testid="edit-user-modal">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user information and role
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Full Name</Label>
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              data-testid="edit-user-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              data-testid="edit-user-email"
            />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
            >
              <SelectTrigger data-testid="edit-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={USER_ROLES.ADMIN}>Admin</SelectItem>
                <SelectItem value={USER_ROLES.USER}>User</SelectItem>
                <SelectItem value={USER_ROLES.REVIEWER}>Reviewer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-department">Department</Label>
            <Input
              id="edit-department"
              value={formData.department}
              onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
              data-testid="edit-user-department"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="save-user-btn">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * User Stats Card
 */
function UserStatsCard({ icon: Icon, title, value, description, color }) {
  const colorClasses = {
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={cn('p-3 rounded-lg', colorClasses[color])}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-muted-foreground">{title}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * User Management Page Component
 */
export function UserManagement() {
  const {
    users,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    userStatistics,
  } = useBEStudy();

  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Filter users by role tab
  const filteredUsers = useMemo(() => {
    let result = users;

    if (activeTab !== 'all') {
      result = result.filter(u => u.role === activeTab);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u =>
        u.name.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        u.department?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [users, activeTab, searchQuery]);

  // Table columns
  const columns = [
    {
      accessorKey: 'name',
      header: 'User',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={row.avatar} />
            <AvatarFallback className="text-xs">
              {row.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{row.name}</p>
            <p className="text-xs text-muted-foreground">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ value }) => <RoleBadge role={value} />,
    },
    {
      accessorKey: 'department',
      header: 'Department',
      cell: ({ value }) => (
        <span className="text-sm text-muted-foreground">
          {value || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ value }) => <UserStatusBadge status={value} />,
    },
    {
      accessorKey: 'lastLogin',
      header: 'Last Login',
      cell: ({ value }) => (
        <span className="text-sm text-muted-foreground">
          {value ? getRelativeTime(value) : 'Never'}
        </span>
      ),
    },
  ];

  // Handlers
  const handleView = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = (user) => {
    toggleUserStatus(user.id);
  };

  const handleConfirmDelete = () => {
    if (selectedUser) {
      deleteUser(selectedUser.id);
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  const handleCreateUser = (data) => {
    createUser(data);
    setShowAddModal(false);
  };

  const handleUpdateUser = (data) => {
    if (selectedUser) {
      updateUser(selectedUser.id, data);
      setSelectedUser(null);
    }
  };

  // Row actions
  const rowActions = (row) => (
    <>
      <DropdownMenuItem onClick={() => handleView(row)} data-testid={`view-user-${row.id}`}>
        <Eye className="h-4 w-4 mr-2" />
        View Details
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleEdit(row)} data-testid={`edit-user-${row.id}`}>
        <Edit className="h-4 w-4 mr-2" />
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleToggleStatus(row)}>
        {row.status === USER_STATUS.ACTIVE ? (
          <>
            <PowerOff className="h-4 w-4 mr-2" />
            Deactivate
          </>
        ) : (
          <>
            <Power className="h-4 w-4 mr-2" />
            Activate
          </>
        )}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem 
        onClick={() => handleDelete(row)}
        className="text-red-600"
        data-testid={`delete-user-${row.id}`}
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete
      </DropdownMenuItem>
    </>
  );

  return (
    <BaseLayout>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">User Management</h1>
            <p className="text-muted-foreground">
              Manage system users and their roles
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <UserStatsCard
            icon={Users}
            title="Total Users"
            value={userStatistics.total}
            color="blue"
          />
          <UserStatsCard
            icon={Shield}
            title="Admins"
            value={userStatistics.admins}
            color="red"
          />
          <UserStatsCard
            icon={User}
            title="Users"
            value={userStatistics.regularUsers}
            color="blue"
          />
          <UserStatsCard
            icon={ClipboardCheck}
            title="Reviewers"
            value={userStatistics.reviewers}
            color="purple"
          />
        </div>

        {/* Users Table with Tabs */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="all" data-testid="tab-all">
                    All ({users.length})
                  </TabsTrigger>
                  <TabsTrigger value={USER_ROLES.ADMIN} data-testid="tab-admin">
                    Admins ({userStatistics.admins})
                  </TabsTrigger>
                  <TabsTrigger value={USER_ROLES.USER} data-testid="tab-user">
                    Users ({userStatistics.regularUsers})
                  </TabsTrigger>
                  <TabsTrigger value={USER_ROLES.REVIEWER} data-testid="tab-reviewer">
                    Reviewers ({userStatistics.reviewers})
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-users-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable
              data={filteredUsers}
              columns={columns}
              rowActions={rowActions}
              emptyMessage="No users found"
              onRowClick={handleView}
            />
          </CardContent>
        </Card>

        {/* Modals */}
        <AddUserModal
          open={showAddModal}
          onOpenChange={setShowAddModal}
          onSubmit={handleCreateUser}
          existingEmails={users.map(u => u.email)}
        />

        <UserDetailModal
          user={selectedUser}
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
        />

        <EditUserModal
          user={selectedUser}
          open={showEditModal}
          onOpenChange={setShowEditModal}
          onSave={handleUpdateUser}
        />

        <DeleteConfirmationModal
          open={showDeleteModal}
          onOpenChange={setShowDeleteModal}
          onConfirm={handleConfirmDelete}
          itemName={selectedUser?.name}
          itemType="user"
        />
      </div>
    </BaseLayout>
  );
}

export default UserManagement;
